/**
 * Pure view logic for the "Seven Free Days test tools" card (Admin Portal →
 * Users → a user's detail). Kept out of the .tsx so jest can test it without
 * React, lucide or Tailwind (same split as the analytics funnel's view logic).
 *
 * The card lets the owner run B13 — the first production proof of the 7-day
 * clock — by clicking instead of an ECS-exec write:
 *   - two per-user switches, `time_bound_trial_enabled` ("7-day clock") and
 *     `revenuecat_paywall_enabled` ("New paywall"), through the existing
 *     PATCH /api/admin/users/{id}/feature-flags (admin/super_admin only);
 *   - "Move to day 6" / "End trial now", through
 *     POST /api/admin/users/{id}/trial-clock/fast-forward (admin/super_admin only,
 *     refused unless the account is a Cedar clock week the trial crons act on AND
 *     its own 7-day clock switch is on — only test accounts carry it);
 *   - a status line from GET /api/admin/users/{id}/trial-clock.
 * Every switch and every move asks first, in the page (no window.confirm).
 *
 * The requests go through the Admin Portal's shared `adminFetch`
 * (web/src/utils/adminApi.ts): the absolute API origin on every host but
 * localhost, and no request at all without an admin session token.
 */

export type FastForwardStage = 'day6' | 'expire';
export const STAGES: FastForwardStage[] = ['day6', 'expire'];

export const CLOCK_FLAG = 'time_bound_trial_enabled';
export const PAYWALL_FLAG = 'revenuecat_paywall_enabled';

export interface Blocker {
  code: string;
  message: string;
}

export interface SwitchState {
  user_value: unknown;
  on: boolean;
  /** On for this account WITHOUT its own switch (for everyone, or the env allowlist). */
  global_on: boolean;
  env_on_reason?: 'everyone' | 'env_allowlist' | null;
  min_build?: number;
}

/**
 * When the push a move leads to goes — computed by the SERVER with the trial cron's
 * own rule (J2's window functions, services/trial_push_timing.py) in the account's
 * own time zone (services/trial_clock_admin.py `push_schedule`). The card never
 * re-states the rule: it shows these instants.
 */
export type PushWhen = 'at' | 'next_run' | 'may_be_skipped' | 'skipped' | 'already_decided' | 'unknown';

export interface PushSchedule {
  push: 'day6_warning' | 'paused';
  when: PushWhen;
  /** The instant the push is due (ISO, UTC) — `when: 'at'`, and the paused push's passed 09:00. */
  at: string | null;
  at_local: string | null;
  /** When its window closes (ISO, UTC). */
  closes_at: string | null;
  closes_at_local: string | null;
  /** The account's IANA time zone, as the trial cron resolves it. */
  timezone: string | null;
  /** What to print for the zone: the IANA name, or "UTC — no time zone on file". */
  timezone_label?: string | null;
  timing: 'local_daytime' | 'next_run' | null;
  /** The run that would send it lands in the account's quiet hours (the orchestrator's own rule). */
  quiet_hours_hold?: boolean | null;
  /** #4119 review L10: when a push held by quiet hours goes instead (ISO), if it waits. */
  quiet_hours_deferred_to?: string | null;
  quiet_hours_deferred_to_local?: string | null;
}

export interface TrialClockStatus {
  user_id: string;
  subscription_status: string | null;
  subscription_tier: string | null;
  trial_starts_at: string | null;
  trial_expires_at: string | null;
  tracking_trial_expires_at: string | null;
  cohort: string;
  is_clock_trial: boolean;
  user_class: string | null;
  warning_sent_48h: boolean;
  /** The reasons that stop every move. */
  blockers: Blocker[];
  /** Every reason each move is refused (the common ones plus the stage's own). */
  stage_blockers?: Partial<Record<FastForwardStage, Blocker[]>>;
  fast_forward_enabled: boolean;
  can_fast_forward: boolean;
  can_move?: Partial<Record<FastForwardStage, boolean>>;
  switches: Record<string, SwitchState>;
  viewer_can_write: boolean;
  /** For a movable account: when each move's push would go if pressed now. */
  push_preview?: Partial<Record<FastForwardStage, PushSchedule | null>>;
}

/** The paths, under the admin API prefix, as `adminFetch` takes them. */
export const ADMIN_PREFIX = '/api/admin';
const seg = (userId: string) => encodeURIComponent(userId);
export const trialClockPath = (userId: string) => `${ADMIN_PREFIX}/users/${seg(userId)}/trial-clock`;
export const fastForwardPath = (userId: string) => `${ADMIN_PREFIX}/users/${seg(userId)}/trial-clock/fast-forward`;
export const featureFlagsPath = (userId: string) => `${ADMIN_PREFIX}/users/${seg(userId)}/feature-flags`;

export function flagPatchBody(flag: string, on: boolean): { flag_name: string; value: boolean } {
  return { flag_name: flag, value: on };
}

export function fastForwardBody(stage: FastForwardStage): { stage: FastForwardStage } {
  return { stage };
}

/**
 * A time, always labelled with its zone ("Sep 26, 2026, 2:05 PM EDT"): the browser's
 * zone unless `timeZone` names another. Never "Invalid Date".
 */
export function formatWhen(iso: string | null, timeZone?: string): string {
  if (!iso) return 'not set';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'not set';
  // Some ICU versions put a narrow no-break space before AM/PM: one plain space everywhere.
  return d
    .toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZoneName: 'short',
      ...(timeZone ? { timeZone } : {}),
    })
    .replace(/\s+/g, ' ');
}

/** One line: the trial's start and end, the cohort, and whether it is a Cedar clock week. */
export function statusLine(s: TrialClockStatus, timeZone?: string): string {
  const kind = s.is_clock_trial ? 'Cedar 7-day clock' : 'not a Cedar 7-day clock';
  const dates = s.trial_starts_at
    ? `Trial started ${formatWhen(s.trial_starts_at, timeZone)} · ends ${formatWhen(s.trial_expires_at, timeZone)}`
    : 'No trial dates yet';
  return `${dates} · cohort ${s.cohort} · ${kind}`;
}

/**
 * What a move changes, and when the pushes then go. A move changes the dates and
 * nothing else; the pushes go out on the real schedule (the 10-minute trial cron's
 * own rules — the ones every real user gets since J2, #4008) and through the
 * account's quiet hours. The backend's `_next_step` (services/trial_clock_admin.py)
 * says the same after the move; the two notes below are pinned equal to it.
 */
export const QUIET_HOURS_NOTE =
  "Pushes follow the real schedule and quiet hours: a push due inside this account's quiet hours (22:00–08:00 " +
  'its time, unless it set others) waits for them to end, unless its window has closed by then — these pushes ' +
  'are push-only.';
export const REAL_USER_TIMING_NOTE =
  'A real user gets these pushes at the same local times: the day-6 warning at 10:00 on day 6, and ' +
  '"Coach Cedar has paused" at 09:00 the morning after the week ends.';

const PUSH_NAME: Record<PushSchedule['push'], string> = {
  day6_warning: 'The day-6 warning ("Coach Cedar pauses tomorrow night")',
  paused: 'The "Coach Cedar has paused" push',
};

/**
 * One sentence: when the push goes, from the server's schedule (the time shown in the
 * account's own zone). Mirrors the backend's `describe_push_schedule`; the TIME comes
 * from the server, so the card and the cron can never disagree about it.
 */
export function pushScheduleSentence(s: PushSchedule | null | undefined): string {
  if (!s) return "Its push goes when the trial cron's own rules say (the time shows once the card has loaded).";
  const name = PUSH_NAME[s.push];
  const tz = s.timezone || undefined;
  const zone = s.timezone_label || s.timezone || '?';
  const held = s.quiet_hours_hold
    ? " That run falls inside this account's quiet hours, which end after the push's window closes: it is " +
      'not sent (the cron does not send it again).'
    : s.quiet_hours_deferred_to
    ? ` That run falls inside this account's quiet hours: the push waits and goes when they end, ` +
      `${formatWhen(s.quiet_hours_deferred_to, tz)}.`
    : '';
  switch (s.when) {
    case 'at':
      return (
        `${name} goes on the trial cron's first run after ${formatWhen(s.at, tz)}, this account's time ` +
        `(${zone}) — within about 10 minutes of it.${held}`
      );
    case 'may_be_skipped':
      return (
        `${name} is due now, but its window closes at ${formatWhen(s.closes_at, tz)} (${zone}) — within the ` +
        "trial cron's 10 minutes: if its next run lands after that, the warning is skipped (it never says " +
        `"tomorrow" on the last day).${held}`
      );
    case 'skipped':
      return `${name} will not go: its window closed at ${formatWhen(s.closes_at, tz)} (${zone}), so the trial cron skips it.`;
    case 'already_decided':
      return (
        `${name} will not go now: one was already sent or skipped for this account in the last 48 hours, and ` +
        'the cron does not decide it again until that is 48 hours old.'
      );
    case 'unknown':
      return `${name} goes when the trial cron's rules say (this account's time zone could not be read here).`;
    default:
      if (s.push === 'paused') {
        return (
          `${name} goes on the trial cron's run after it ends the trial (within about 20 minutes)` +
          (s.at ? `: its first 09:00 after the end (${formatWhen(s.at, tz)}) has already come.` : '.') +
          held
        );
      }
      return `${name} goes on the trial cron's next run (within 10 minutes).${held}`;
  }
}

export const STAGE_COPY: Record<FastForwardStage, { button: string; title: string; body: string; confirm: string }> = {
  day6: {
    button: 'Move to day 6',
    title: 'Move this trial to day 6?',
    body:
      'Sets the trial to end 24 hours from now (the start date stays; it never pushes the end later). ' +
      `{schedule} ${QUIET_HOURS_NOTE} ${REAL_USER_TIMING_NOTE} This is a real change to a production account.`,
    confirm: 'Yes, move to day 6',
  },
  expire: {
    button: 'End trial now',
    title: 'End this trial now?',
    body:
      'Sets the trial to have ended 5 minutes ago. On its next run (within 10 minutes) the trial cron expires it: ' +
      'coaching pauses and the plan stays visible. {schedule} Never if the account buys first. ' +
      `${QUIET_HOURS_NOTE} ${REAL_USER_TIMING_NOTE} This is a real change to a production account.`,
    confirm: 'Yes, end the trial',
  },
};

/**
 * A move's push would be spent without landing: the day-6 warning may be skipped (its
 * window closes before the next run), or the run lands in the account's quiet hours and
 * they end after the push's window closes (a push they merely hold waits for their end —
 * #4119 review L10). A move cannot be undone, so the confirm says so plainly.
 */
export function stageWarning(stage: FastForwardStage, status: TrialClockStatus | null): string | null {
  const p = status?.push_preview?.[stage];
  if (!p) return null;
  const test = stage === 'day6' ? 'the day-6 test' : 'the "Coach Cedar has paused" test';
  const why: string[] = [];
  if (p.when === 'may_be_skipped') why.push('skipped (its window closes before the next run may land)');
  if (p.quiet_hours_hold) why.push("held by this account's quiet hours past its window (not sent)");
  if (!why.length) return null;
  return (
    `Warning: the push will likely be ${why.join(' or ')}. This move can't be undone, so ${test} on this ` +
    'account would be spent. Wait until its time comes round, or use another test account.'
  );
}

/** The in-page question a move asks, with WHEN its push goes by the server's preview. */
export function stageConfirmCopy(
  stage: FastForwardStage,
  status: TrialClockStatus | null,
): { title: string; body: string; confirm: string } {
  const copy = STAGE_COPY[stage];
  return { ...copy, body: copy.body.replace('{schedule}', pushScheduleSentence(status?.push_preview?.[stage])) };
}

/** The two switches' labels. */
export const SWITCH_LABELS: Record<string, string> = {
  [CLOCK_FLAG]: '7-day clock',
  [PAYWALL_FLAG]: 'New paywall',
};

/** The in-page question a switch asks before it changes the account. */
export function switchConfirmCopy(
  flag: string,
  next: boolean,
  minBuild?: number,
): { title: string; body: string; confirm: string } {
  const label = SWITCH_LABELS[flag] || flag;
  let effect: string;
  if (flag === CLOCK_FLAG) {
    effect = next
      ? "This account's first confirmed goal starts a 7-day clock instead of the paywall, and this card may then " +
        'move that clock.'
      : 'Unless the clock is on for everyone, a first goal shows the paywall again. A clock that already started ' +
        'keeps its dates, and this card can no longer move it.';
  } else {
    effect = next
      ? `On build ${minBuild ?? 'the minimum'} or later, the app shows the new RevenueCat paywall instead of the ` +
        'old paywall.'
      : 'Unless the new paywall is on for everyone, the app shows the old paywall.';
  }
  return {
    title: `Turn ${label} ${next ? 'on' : 'off'} for this account?`,
    body: `Sets ${flag} = ${next} on this account. ${effect} This is a real change to a production account.`,
    confirm: `Yes, turn ${label} ${next ? 'on' : 'off'}`,
  };
}

/** The small print under a switch: the flag, why it is already on (if it is), the min build. */
export function switchHint(flag: string, sw: SwitchState | undefined): string {
  const name = flag === PAYWALL_FLAG && sw?.min_build ? `${flag} (builds ${sw.min_build}+)` : flag;
  if (sw?.env_on_reason === 'env_allowlist') return `${name} · on for this account by the env allowlist`;
  if (sw?.global_on) return `${name} · on for everyone`;
  return name;
}

/** The reasons one move is refused: the stage's own list when the server sent it. */
export function stageBlockers(s: TrialClockStatus, stage: FastForwardStage): Blocker[] {
  return s.stage_blockers?.[stage] ?? s.blockers;
}

/** Why a move's button is disabled, or null when it may be pressed. */
export function fastForwardDisabledReason(s: TrialClockStatus | null, stage?: FastForwardStage): string | null {
  if (!s) return 'Loading…';
  if (!s.viewer_can_write) return 'Only an admin or super admin can move a trial.';
  if (!s.fast_forward_enabled) return 'The fast-forward is switched off (FF_ADMIN_TRIAL_CLOCK_FAST_FORWARD).';
  const blockers = stage ? stageBlockers(s, stage) : s.blockers;
  if (blockers.length) return blockers.map((b) => b.message).join(' ');
  if (stage === 'day6' && s.push_preview?.day6?.when === 'skipped') {
    return (
      "The day-6 warning would be skipped if the week moved now (its window has already closed, at " +
      `${formatWhen(s.push_preview.day6.closes_at, s.push_preview.day6.timezone || undefined)}): the move ` +
      "would spend this account's day-6 test. Try again later in the day."
    );
  }
  return null;
}

/**
 * The reasons to show under the two buttons: once when both moves share them,
 * otherwise per move ("Move to day 6: …").
 */
export function disabledReasonLines(s: TrialClockStatus | null): string[] {
  const reasons = STAGES.map((stage) => ({ stage, reason: fastForwardDisabledReason(s, stage) }));
  if (reasons.every((r) => r.reason === reasons[0].reason)) return reasons[0].reason ? [reasons[0].reason] : [];
  return reasons.filter((r) => r.reason).map((r) => `${STAGE_COPY[r.stage].button}: ${r.reason}`);
}

/** A readable error for a failed call: the server's own words where it gave them. */
export function describeError(status: number, detail: unknown): string {
  const d = detail as { message?: string; blockers?: Blocker[] } | string | null | undefined;
  if (d && typeof d === 'object') {
    const parts = [d.message, ...(d.blockers || []).map((b) => b.message)].filter(Boolean);
    if (parts.length) return parts.join(' ');
  }
  if (typeof d === 'string' && d) return d;
  if (status === 403) return 'Your admin role cannot do this (admin or super admin only).';
  if (status === 401) return 'Your admin session has expired. Sign in again.';
  return `Request failed (HTTP ${status}).`;
}

/** The `detail` of a FastAPI error body (its text, as `adminFetch` hands it over), or null. */
export function errorDetail(bodyText: string): unknown {
  if (!bodyText) return null;
  try {
    const parsed = JSON.parse(bodyText);
    return parsed && typeof parsed === 'object' && 'detail' in parsed ? parsed.detail : null;
  } catch {
    return null;
  }
}

export const NOT_JSON_MESSAGE = 'Unexpected answer from the server (not JSON) — nothing was confirmed.';
